// Shared helpers for the blog pages (portfolio slide, post page, writer).
import { marked } from "marked";
import DOMPurify from "dompurify";

// In production Caddy forwards /api/* to the Go blog API on the same domain.
// Locally (parcel on localhost:1234) the API runs on localhost:8080.
const LOCAL = ["localhost", "127.0.0.1"].includes(location.hostname);
export const API_BASE = LOCAL ? "http://localhost:8080" : "";
const TIMEOUT_MS = 10000; // give up instead of "Loading…" forever

export class ApiError extends Error {
  constructor(message, status, fields) {
    super(message);
    this.status = status;
    this.fields = fields || {};
  }
}

export async function api(path, { method = "GET", body, token } = {}) {
  const headers = {};
  if (body !== undefined) headers["Content-Type"] = "application/json";
  if (token) headers.Authorization = `Bearer ${token}`;
  let res;
  const timeout = new AbortController();
  const timer = setTimeout(() => timeout.abort(), TIMEOUT_MS);
  try {
    res = await fetch(API_BASE + path, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: timeout.signal,
    });
  } catch {
    throw new ApiError(
      timeout.signal.aborted ? "The blog server is taking too long to answer." : "Can't reach the blog server.",
      0
    );
  } finally {
    clearTimeout(timer);
  }
  if (res.status === 204) return null;
  let data = null;
  try {
    data = await res.json();
  } catch {
    /* not JSON (e.g. an HTML error page from the proxy) */
  }
  if (!res.ok) {
    throw new ApiError(data?.error || `Request failed (HTTP ${res.status}).`, res.status, data?.fields);
  }
  return data;
}

marked.use({ gfm: true });

// Links to other sites open in a new tab
DOMPurify.addHook("afterSanitizeAttributes", (node) => {
  if (node.tagName === "A" && /^https?:\/\//.test(node.getAttribute("href") || "")) {
    node.setAttribute("target", "_blank");
    node.setAttribute("rel", "noopener noreferrer");
  }
});

// Markdown -> safe HTML
export function renderMarkdown(md) {
  return DOMPurify.sanitize(marked.parse(md || ""));
}

const dateFmt = new Intl.DateTimeFormat("en", { day: "numeric", month: "short", year: "numeric" });

export function formatDate(iso) {
  return iso ? dateFmt.format(new Date(iso)) : "";
}

export function postUrl(slug) {
  return `post.html?slug=${encodeURIComponent(slug)}`;
}

const ESCAPES = { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" };
export function esc(s) {
  return String(s ?? "").replace(/[&<>"']/g, (c) => ESCAPES[c]);
}
